// src/lib/cache/estimates.js
//
// Per-entity cache wiring for estimates. Uses the deferredField hybrid:
// estimate_number is server-fabricated by the next_estimate_number RPC
// (year-scoped, per-company counter, row-locked, UNIQUE-constrained), so the
// optimistic row carries a sentinel for that field and the real number snaps
// in from the server response on reconcile. Predicting it client-side would
// race the RPC and the optimistic UI would visibly flip on reconcile.

import {
  getEstimates,
  createEstimate,
  updateEstimate,
  deleteEstimate,
} from '../api/financial/estimates.js'
import { supabaseData } from '../supabaseData.js'
import {
  useEntityListQuery,
  useOptimisticListMutation,
  useStandardDeleteMutation,
  defaultCreateReconcile,
  defaultUpdateReconcile,
  unwrapList,
} from './entityCache.js'

const ENTITY = 'estimates'
const DEFERRED_NUMBER = 'estimate_number'
const NUMBER_PLACEHOLDER = '…'

export const useEstimatesQuery = () =>
  useEntityListQuery(ENTITY, unwrapList('estimates', getEstimates))

export const useCreateEstimateMutation = () =>
  useOptimisticListMutation(ENTITY, {
    deferredField: DEFERRED_NUMBER,
    mutationFn: async (estimateData) => {
      const { estimate_number: _ignored, ...safe } = estimateData
      const estimateNumber = await supabaseData.generateEstimateNumber(safe.company_id)
      return createEstimate({ ...safe, estimate_number: estimateNumber })
    },
    applyOptimistic: (prev, vars, { tempId }) => {
      const now = new Date().toISOString()
      const { estimate_number: _ignored, ...safe } = vars
      return [{
        status: 'draft',
        ...safe,
        id: tempId,
        estimate_number: NUMBER_PLACEHOLDER,
        created_at: now,
        updated_at: now,
      }, ...prev]
    },
    reconcile: defaultCreateReconcile,
  })

export const useUpdateEstimateMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: ({ id, ...patch }) => updateEstimate(id, patch),
    applyOptimistic: (prev, vars) =>
      prev.map((row) => (row.id === vars.id ? { ...row, ...vars } : row)),
    reconcile: defaultUpdateReconcile,
  })

export const useDeleteEstimateMutation = () =>
  useStandardDeleteMutation(ENTITY, deleteEstimate)
